/**
 * Title/Escrow Company Service
 *
 * Frontend service for managing title and escrow companies
 * with fee schedules linked to Encompass itemization fees.
 */

import { supabase } from '@/utils/supabase';
import {
  TitleEscrowCompany,
  TitleEscrowCompanyRow,
  CompanyFeeSchedule,
  CompanyFeeScheduleRow,
  CompanyFeeScheduleWithFee,
  GroupedFeeTiers,
  FeeTier,
  CompanyFilters,
  CompanyStats,
  CreateTitleEscrowCompanyInput,
  UpdateTitleEscrowCompanyInput,
  CreateFeeScheduleInput,
  transformCompanyRow,
  transformFeeScheduleRow,
  PricingType
} from '@/types/title-escrow';
import { createComponentLogger } from '@/utils/logger';

const log = createComponentLogger('TitleEscrowService', 'title-escrow');

/**
 * Get all companies with optional filters
 */
export async function getCompanies(filters?: CompanyFilters): Promise<TitleEscrowCompany[]> {
  log.info('Fetching companies', { filters });

  let query = supabase
    .from('title_escrow_companies')
    .select('*')
    .order('name');

  if (filters?.companyType && filters.companyType !== 'all') {
    query = query.eq('company_type', filters.companyType);
  }

  if (filters?.isActive !== undefined) {
    query = query.eq('is_active', filters.isActive);
  }

  if (filters?.isPreferred !== undefined) {
    query = query.eq('is_preferred', filters.isPreferred);
  }

  if (filters?.state) {
    query = query.eq('state', filters.state);
  }

  if (filters?.search) {
    query = query.or(`name.ilike.%${filters.search}%,officer_first_name.ilike.%${filters.search}%,officer_last_name.ilike.%${filters.search}%,city.ilike.%${filters.search}%`);
  }

  const { data, error } = await query;

  if (error) {
    log.error('Failed to fetch companies', { error: error.message });
    throw new Error(`Failed to fetch companies: ${error.message}`);
  }

  return (data as TitleEscrowCompanyRow[]).map(transformCompanyRow);
}

/**
 * Get company by ID with fee schedules
 */
export async function getCompanyById(id: string): Promise<TitleEscrowCompany | null> {
  log.info('Fetching company by ID', { id });

  const { data, error } = await supabase
    .from('title_escrow_companies')
    .select('*')
    .eq('id', id)
    .single();

  if (error) {
    if (error.code === 'PGRST116') {
      return null;
    }
    log.error('Failed to fetch company', { id, error: error.message });
    throw new Error(`Failed to fetch company: ${error.message}`);
  }

  return transformCompanyRow(data as TitleEscrowCompanyRow);
}

/**
 * Create a new company
 */
export async function createCompany(input: CreateTitleEscrowCompanyInput): Promise<TitleEscrowCompany> {
  log.info('Creating company', { name: input.name, type: input.companyType });

  // Get current user's organization
  const { data: userData, error: userError } = await supabase.auth.getUser();
  if (userError || !userData.user) {
    throw new Error('Not authenticated');
  }

  const { data: userProfile, error: profileError } = await supabase
    .from('users')
    .select('organization_id')
    .eq('id', userData.user.id)
    .single();

  if (profileError || !userProfile) {
    throw new Error('Failed to get user organization');
  }

  const { data, error } = await supabase
    .from('title_escrow_companies')
    .insert({
      organization_id: userProfile.organization_id,
      name: input.name,
      company_type: input.companyType,
      legal_name: input.legalName || null,
      address_line1: input.addressLine1,
      address_line2: input.addressLine2 || null,
      city: input.city,
      state: input.state,
      zip: input.zip,
      main_phone: input.mainPhone || null,
      main_email: input.mainEmail || null,
      fax: input.fax || null,
      website: input.website || null,
      officer_first_name: input.officerFirstName || null,
      officer_last_name: input.officerLastName || null,
      officer_email: input.officerEmail || null,
      officer_phone: input.officerPhone || null,
      contact_first_name: input.contactFirstName || null,
      contact_last_name: input.contactLastName || null,
      is_preferred: input.isPreferred || false,
      notes: input.notes || null,
      created_by: userData.user.id
    })
    .select()
    .single();

  if (error) {
    log.error('Failed to create company', { error: error.message });
    throw new Error(`Failed to create company: ${error.message}`);
  }

  log.info('Company created', { id: data.id, name: input.name });
  return transformCompanyRow(data as TitleEscrowCompanyRow);
}

/**
 * Update a company
 */
export async function updateCompany(id: string, input: UpdateTitleEscrowCompanyInput): Promise<TitleEscrowCompany> {
  log.info('Updating company', { id });

  const updateData: Record<string, unknown> = {};

  if (input.name !== undefined) {
updateData.name = input.name;
}
  if (input.companyType !== undefined) {
updateData.company_type = input.companyType;
}
  if (input.legalName !== undefined) {
updateData.legal_name = input.legalName || null;
}
  if (input.addressLine1 !== undefined) {
updateData.address_line1 = input.addressLine1;
}
  if (input.addressLine2 !== undefined) {
updateData.address_line2 = input.addressLine2 || null;
}
  if (input.city !== undefined) {
updateData.city = input.city;
}
  if (input.state !== undefined) {
updateData.state = input.state;
}
  if (input.zip !== undefined) {
updateData.zip = input.zip;
}
  if (input.mainPhone !== undefined) {
updateData.main_phone = input.mainPhone || null;
}
  if (input.mainEmail !== undefined) {
updateData.main_email = input.mainEmail || null;
}
  if (input.fax !== undefined) {
updateData.fax = input.fax || null;
}
  if (input.website !== undefined) {
updateData.website = input.website || null;
}
  if (input.officerFirstName !== undefined) {
updateData.officer_first_name = input.officerFirstName || null;
}
  if (input.officerLastName !== undefined) {
updateData.officer_last_name = input.officerLastName || null;
}
  if (input.officerEmail !== undefined) {
updateData.officer_email = input.officerEmail || null;
}
  if (input.officerPhone !== undefined) {
updateData.officer_phone = input.officerPhone || null;
}
  if (input.contactFirstName !== undefined) {
updateData.contact_first_name = input.contactFirstName || null;
}
  if (input.contactLastName !== undefined) {
updateData.contact_last_name = input.contactLastName || null;
}
  if (input.isActive !== undefined) {
updateData.is_active = input.isActive;
}
  if (input.isPreferred !== undefined) {
updateData.is_preferred = input.isPreferred;
}
  if (input.notes !== undefined) {
updateData.notes = input.notes || null;
}

  const { data, error } = await supabase
    .from('title_escrow_companies')
    .update(updateData)
    .eq('id', id)
    .select()
    .single();

  if (error) {
    log.error('Failed to update company', { id, error: error.message });
    throw new Error(`Failed to update company: ${error.message}`);
  }

  log.info('Company updated', { id });
  return transformCompanyRow(data as TitleEscrowCompanyRow);
}

/**
 * Delete a company (soft delete by setting is_active = false)
 */
export async function deleteCompany(id: string): Promise<void> {
  log.info('Deleting company', { id });

  const { error } = await supabase
    .from('title_escrow_companies')
    .update({ is_active: false })
    .eq('id', id);

  if (error) {
    log.error('Failed to delete company', { id, error: error.message });
    throw new Error(`Failed to delete company: ${error.message}`);
  }

  log.info('Company deleted (soft)', { id });
}

/**
 * Get fee schedule for a company
 */
export async function getFeeSchedule(companyId: string): Promise<CompanyFeeScheduleWithFee[]> {
  log.info('Fetching fee schedule', { companyId });

  const { data, error } = await supabase
    .from('company_fee_schedules')
    .select(`
      *,
      encompass_itemization_fees (
        id,
        name,
        encompass_fee_id,
        sections,
        ucd_fee
      )
    `)
    .eq('company_id', companyId)
    .eq('is_active', true)
    .order('created_at');

  if (error) {
    log.error('Failed to fetch fee schedule', { companyId, error: error.message });
    throw new Error(`Failed to fetch fee schedule: ${error.message}`);
  }

  return (data || []).map((row: CompanyFeeScheduleRow & { encompass_itemization_fees: { id: string; name: string; encompass_fee_id: string; sections: string[]; ucd_fee: string | null } }) => ({
    ...transformFeeScheduleRow(row),
    fee: {
      id: row.encompass_itemization_fees.id,
      name: row.encompass_itemization_fees.name,
      encompassFeeId: row.encompass_itemization_fees.encompass_fee_id,
      sections: row.encompass_itemization_fees.sections,
      ucdFee: row.encompass_itemization_fees.ucd_fee
    }
  }));
}

/**
 * Get fee schedule grouped by fee (for tiered pricing display)
 */
export async function getGroupedFeeSchedule(companyId: string): Promise<GroupedFeeTiers[]> {
  const fees = await getFeeSchedule(companyId);

  // Group by fee ID
  const grouped = new Map<string, GroupedFeeTiers>();

  for (const schedule of fees) {
    const existing = grouped.get(schedule.feeId);

    const tier: FeeTier = {
      id: schedule.id,
      minLoanAmount: schedule.minLoanAmount,
      maxLoanAmount: schedule.maxLoanAmount,
      amount: schedule.amount,
      percentageRate: schedule.percentageRate,
      updatedAt: schedule.updatedAt
    };

    if (existing) {
      existing.tiers.push(tier);
    } else {
      grouped.set(schedule.feeId, {
        feeId: schedule.feeId,
        feeName: schedule.fee.name,
        encompassFeeId: schedule.fee.encompassFeeId,
        sections: schedule.fee.sections,
        pricingType: schedule.pricingType,
        isConditional: schedule.isConditional,
        conditionDescription: schedule.conditionDescription,
        tiers: [tier]
      });
    }
  }

  // Sort tiers by min loan amount
  for (const group of grouped.values()) {
    group.tiers.sort((a, b) => (a.minLoanAmount || 0) - (b.minLoanAmount || 0));
  }

  return Array.from(grouped.values());
}

/**
 * Add a fee to a company's schedule
 */
export async function addFeeToSchedule(input: CreateFeeScheduleInput): Promise<CompanyFeeSchedule> {
  log.info('Adding fee to schedule', { companyId: input.companyId, feeId: input.feeId });

  const { data, error } = await supabase
    .from('company_fee_schedules')
    .insert({
      company_id: input.companyId,
      fee_id: input.feeId,
      pricing_type: input.pricingType,
      amount: input.amount || null,
      min_loan_amount: input.minLoanAmount || null,
      max_loan_amount: input.maxLoanAmount || null,
      percentage_rate: input.percentageRate || null,
      calculation_base: input.calculationBase || null,
      min_amount: input.minAmount || null,
      max_amount: input.maxAmount || null,
      is_conditional: input.isConditional || false,
      condition_description: input.conditionDescription || null,
      notes: input.notes || null
    })
    .select()
    .single();

  if (error) {
    log.error('Failed to add fee to schedule', { error: error.message });
    throw new Error(`Failed to add fee to schedule: ${error.message}`);
  }

  log.info('Fee added to schedule', { id: data.id });
  return transformFeeScheduleRow(data as CompanyFeeScheduleRow);
}

/**
 * Add multiple tiers for a tiered fee
 */
export async function addTieredFee(
  companyId: string,
  feeId: string,
  tiers: Array<{ minLoanAmount: number; maxLoanAmount: number | null; amount: number }>,
  options?: { isConditional?: boolean; conditionDescription?: string; notes?: string }
): Promise<CompanyFeeSchedule[]> {
  log.info('Adding tiered fee', { companyId, feeId, tierCount: tiers.length });

  const inserts = tiers.map((tier) => ({
    company_id: companyId,
    fee_id: feeId,
    pricing_type: 'tiered' as PricingType,
    amount: tier.amount,
    min_loan_amount: tier.minLoanAmount,
    max_loan_amount: tier.maxLoanAmount,
    is_conditional: options?.isConditional || false,
    condition_description: options?.conditionDescription || null,
    notes: options?.notes || null
  }));

  const { data, error } = await supabase
    .from('company_fee_schedules')
    .insert(inserts)
    .select();

  if (error) {
    log.error('Failed to add tiered fee', { error: error.message });
    throw new Error(`Failed to add tiered fee: ${error.message}`);
  }

  log.info('Tiered fee added', { count: data.length });
  return (data as CompanyFeeScheduleRow[]).map(transformFeeScheduleRow);
}

/**
 * Update a fee schedule entry
 */
export async function updateFeeSchedule(
  id: string,
  input: Partial<CreateFeeScheduleInput>
): Promise<CompanyFeeSchedule> {
  log.info('Updating fee schedule', { id });

  const updateData: Record<string, unknown> = {};

  if (input.pricingType !== undefined) {
updateData.pricing_type = input.pricingType;
}
  if (input.amount !== undefined) {
updateData.amount = input.amount;
}
  if (input.minLoanAmount !== undefined) {
updateData.min_loan_amount = input.minLoanAmount;
}
  if (input.maxLoanAmount !== undefined) {
updateData.max_loan_amount = input.maxLoanAmount;
}
  if (input.percentageRate !== undefined) {
updateData.percentage_rate = input.percentageRate;
}
  if (input.calculationBase !== undefined) {
updateData.calculation_base = input.calculationBase;
}
  if (input.minAmount !== undefined) {
updateData.min_amount = input.minAmount;
}
  if (input.maxAmount !== undefined) {
updateData.max_amount = input.maxAmount;
}
  if (input.isConditional !== undefined) {
updateData.is_conditional = input.isConditional;
}
  if (input.conditionDescription !== undefined) {
updateData.condition_description = input.conditionDescription;
}
  if (input.notes !== undefined) {
updateData.notes = input.notes;
}

  const { data, error } = await supabase
    .from('company_fee_schedules')
    .update(updateData)
    .eq('id', id)
    .select()
    .single();

  if (error) {
    log.error('Failed to update fee schedule', { id, error: error.message });
    throw new Error(`Failed to update fee schedule: ${error.message}`);
  }

  log.info('Fee schedule updated', { id });
  return transformFeeScheduleRow(data as CompanyFeeScheduleRow);
}

/**
 * Remove a fee from schedule (soft delete)
 */
export async function removeFeeFromSchedule(id: string): Promise<void> {
  log.info('Removing fee from schedule', { id });

  const { error } = await supabase
    .from('company_fee_schedules')
    .update({ is_active: false })
    .eq('id', id);

  if (error) {
    log.error('Failed to remove fee from schedule', { id, error: error.message });
    throw new Error(`Failed to remove fee from schedule: ${error.message}`);
  }

  log.info('Fee removed from schedule', { id });
}

/**
 * Remove all tiers for a fee (for replacing tiered pricing)
 */
export async function removeAllFeeTiers(companyId: string, feeId: string): Promise<void> {
  log.info('Removing all fee tiers', { companyId, feeId });

  const { error } = await supabase
    .from('company_fee_schedules')
    .update({ is_active: false })
    .eq('company_id', companyId)
    .eq('fee_id', feeId);

  if (error) {
    log.error('Failed to remove fee tiers', { companyId, feeId, error: error.message });
    throw new Error(`Failed to remove fee tiers: ${error.message}`);
  }

  log.info('All fee tiers removed', { companyId, feeId });
}

/**
 * Get company stats
 */
export async function getCompanyStats(): Promise<CompanyStats> {
  log.info('Fetching company stats');

  const { data, error } = await supabase
    .from('title_escrow_companies')
    .select('company_type, is_active, is_preferred');

  if (error) {
    log.error('Failed to fetch company stats', { error: error.message });
    throw new Error(`Failed to fetch company stats: ${error.message}`);
  }

  const companies = data || [];

  return {
    total: companies.length,
    title: companies.filter((c) => c.company_type === 'title').length,
    escrow: companies.filter((c) => c.company_type === 'escrow').length,
    both: companies.filter((c) => c.company_type === 'both').length,
    preferred: companies.filter((c) => c.is_preferred).length,
    active: companies.filter((c) => c.is_active).length
  };
}

/**
 * Export service functions
 */
export const titleEscrowService = {
  getCompanies,
  getCompanyById,
  createCompany,
  updateCompany,
  deleteCompany,
  getFeeSchedule,
  getGroupedFeeSchedule,
  addFeeToSchedule,
  addTieredFee,
  updateFeeSchedule,
  removeFeeFromSchedule,
  removeAllFeeTiers,
  getCompanyStats
};

export default titleEscrowService;
